import type { Map as GlobeMap } from 'maplibre-gl';
import { GlobeSource, globeFeature } from './globe-source.js';
import { globePath } from './globe-geometry.js';
import { createMapLibreProjection } from './globe-projection.js';
import { createGlobeLabelCanvas } from './globe-labels.js';

export interface GlobePoint { lat: number; lng: number; label?: string; color?: string }
export interface GlobeSegmentLabel { label: string; midLat: number; midLng: number }
export type GlobeDistanceCallbacks = {
  onAddWaypoint: (latLng: GlobePoint) => void;
  onMoveWaypoint: (index: number, latLng: GlobePoint) => void;
  onMoveWaypointComplete: (index: number) => void;
  onClickWaypoint: (index: number) => void;
};
export interface GlobeDistanceRuntime { getViewer: () => GlobeMap | null }

export function createGlobeDistanceCapability(runtime: GlobeDistanceRuntime) {
  let source: GlobeSource | null = null;
  let dispose: (() => void) | null = null;
  let points: GlobePoint[] = [];
  function syncWaypoints(waypoints: GlobePoint[], polygonClosed: boolean, segmentLabels: GlobeSegmentLabel[], decompositionFaces?: GlobePoint[][]): void {
    const map = runtime.getViewer();
    if (!map) return;
    if (source?.map !== map) source = new GlobeSource(map, 'distance');
    points = waypoints;
    const features = waypoints.length > 1 ? [globeFeature('distance-line', { type: 'LineString', coordinates: globePath(waypoints, true, polygonClosed) })] : [];
    if (polygonClosed && waypoints.length >= 3) {
      const colors = ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ec4899'];
      (decompositionFaces?.length ? decompositionFaces : [waypoints]).forEach((face, index) => features.push(globeFeature(`distance-face-${index}`, { type: 'Polygon', coordinates: [globePath(face, true, true)] }, { fill: colors[index % colors.length], fillOpacity: decompositionFaces?.length ? 0.22 : 0.08 })));
    }
    waypoints.forEach((point, index) => {
      const image = document.createElement('canvas'); image.width = 60; image.height = 60;
      const context = image.getContext('2d')!; context.scale(2, 2);
      context.beginPath(); context.arc(15, 15, 13.5, 0, Math.PI * 2); context.fillStyle = point.color ?? '#3b82f6'; context.fill();
      context.strokeStyle = '#ffffff'; context.lineWidth = 2; context.stroke(); context.fillStyle = '#ffffff';
      context.font = 'bold 12px sans-serif'; context.textAlign = 'center'; context.textBaseline = 'middle'; context.fillText(point.label ?? String(index + 1), 15, 15);
      features.push(globeFeature(`distance-wp-${index}`, { type: 'Point', coordinates: [point.lng, point.lat] }, { image: source!.image(`wp-${index}`, image, 2) }));
    });
    segmentLabels.forEach((segment, index) => {
      const label = createGlobeLabelCanvas(segment.label);
      features.push(globeFeature(`distance-label-${index}`, { type: 'Point', coordinates: [segment.midLng, segment.midLat] }, { image: source!.image(`label-${index}`, label.image, label.pixelRatio), offset: label.offset }));
    });
    source.set(features);
  }
  function disableInteraction(): void { dispose?.(); dispose = null; }
  function enableInteraction(callbacks: GlobeDistanceCallbacks): void {
    disableInteraction();
    const map = runtime.getViewer();
    if (!map) return;
    const canvas = map.getCanvas();
    const projection = createMapLibreProjection(map);
    let drag: { pointerId: number; index: number; x: number; y: number; moved: boolean; pan: boolean } | null = null;
    const position = (event: PointerEvent) => { const box = canvas.getBoundingClientRect(); return { x: event.clientX - box.left, y: event.clientY - box.top }; };
    const down = (event: PointerEvent) => {
      if (event.button !== 0) return;
      const p = position(event);
      const index = points.findIndex(point => { const screen = projection.project(point); return screen && Math.hypot(screen.x - p.x, screen.y - p.y) <= 18; });
      drag = { pointerId: event.pointerId, index, ...p, moved: false, pan: map.dragPan.isEnabled() };
      if (index >= 0) { map.dragPan.disable(); canvas.setPointerCapture(event.pointerId); event.preventDefault(); }
    };
    const move = (event: PointerEvent) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const p = position(event);
      drag.moved ||= Math.hypot(p.x - drag.x, p.y - drag.y) >= 6;
      if (drag.index >= 0 && drag.moved) { const point = projection.unproject(p); if (point) callbacks.onMoveWaypoint(drag.index, point); }
    };
    const end = (event: PointerEvent) => {
      const completed = drag; drag = null;
      if (!completed) return;
      if (completed.pan) map.dragPan.enable();
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      if (event.type === 'pointercancel') { if (completed.index >= 0 && completed.moved) callbacks.onMoveWaypointComplete(completed.index); return; }
      if (completed.index >= 0) {
        if (completed.moved) callbacks.onMoveWaypointComplete(completed.index); else callbacks.onClickWaypoint(completed.index);
      } else if (!completed.moved) { const point = projection.unproject(position(event)); if (point) callbacks.onAddWaypoint(point); }
    };
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    dispose = () => { canvas.removeEventListener('pointerdown', down); canvas.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); window.removeEventListener('pointercancel', end); if (drag?.pan) map.dragPan.enable(); drag = null; };
  }
  function destroy(): void { disableInteraction(); source?.destroy(); source = null; points = []; }
  return { syncWaypoints, enableInteraction, disableInteraction, destroy };
}
